<template>
  <div class="countdown-timer flex  items-center justify-center p-4">
    <div v-if="!isExpired" class="grid auto-cols-max grid-flow-col gap-5 text-center">
      <div v-if="formattedTime.days > 0" class="flex flex-col">
        <span class="countdown font-mono text-5xl">
          <span :style="`--value:${displayTime.days};`" aria-live="polite" :aria-label="displayTime.days">{{ displayTime.days }}</span>
        </span>
        {{ $t('day', formattedTime.days) }}
      </div>
      <div v-if="formattedTime.hours > 0 || formattedTime.days > 0" class="flex flex-col">
        <span class="countdown font-mono text-5xl">
          <span :style="`--value:${displayTime.hours};`" aria-live="polite" :aria-label="displayTime.hours">{{ displayTime.hours }}</span>
        </span>
        {{ $t('hour', formattedTime.hours) }}
      </div>
      <div class="flex flex-col">
        <span class="countdown font-mono text-5xl">
          <span :style="`--value:${displayTime.minutes};`" aria-live="polite" :aria-label="displayTime.minutes">{{ displayTime.minutes }}</span>
        </span>
        {{ $t('minute', formattedTime.minutes) }}
      </div>
      <div class="flex flex-col">
        <span class="countdown font-mono text-5xl">
          <span :style="`--value:${displayTime.seconds};`" aria-live="polite" :aria-label="displayTime.seconds">{{ displayTime.seconds }}</span>
        </span>
        {{ $t('second', formattedTime.seconds) }}
      </div>
    </div>
    <div v-else class="text-red-500 font-semibold text-lg text-center">
      {{expiredMessage}}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  expiredMessage: {
    type: String,
    default: 'Time expired!'
  },
  targetDate: {
    type: [String, Date, Number],
    required: true
  }
});

const emit = defineEmits(['expired']);

const currentTime = ref(Date.now());
const intervalId = ref(null);
const isExpired = ref(false);

// Computed property to calculate remaining time
const remainingTime = computed(() => {
  const target = new Date(props.targetDate).getTime();
  const now = currentTime.value;
  const difference = target - now;
  
  if (difference <= 0) {
    return 0;
  }
  
  return difference;
});

// Computed property to format time
const formattedTime = computed(() => {
  const totalSeconds = Math.floor(remainingTime.value / 1000);
  
  if (totalSeconds <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
  }
  
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;
  
  return {
    days: days,
    hours: hours,
    minutes: minutes,
    seconds: seconds
  };
});

// Computed property for padded display values
const displayTime = computed(() => {
  return {
    days: String(formattedTime.value.days).padStart(2, '0'),
    hours: String(formattedTime.value.hours).padStart(2, '0'),
    minutes: String(formattedTime.value.minutes).padStart(2, '0'),
    seconds: String(formattedTime.value.seconds).padStart(2, '0')
  };
});

// Start the countdown timer
const startTimer = () => {
  intervalId.value = setInterval(() => {
    currentTime.value = Date.now();
    
    if (remainingTime.value <= 0 && !isExpired.value) {
      isExpired.value = true;
      emit('expired');
      stopTimer();
    }
  }, 1000);
};

// Stop the countdown timer
const stopTimer = () => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
    intervalId.value = null;
  }
};

// Watch for target date changes
watch(() => props.targetDate, () => {
  isExpired.value = false;
  currentTime.value = Date.now();
  
  if (remainingTime.value <= 0) {
    isExpired.value = true;
    emit('expired');
  }
}, { immediate: true });

onMounted(() => {
  startTimer();
});

onUnmounted(() => {
  stopTimer();
});
</script>

 